import { CheckBox, Column, FormInput, FormLocation } from '@/components'
import { AssessmentStepKeys } from '@/enums'
import { useFormContext, useNextLoading, useOnNextClick } from '@/providers'
import { spread, useToggable } from '@/utils'
import { equals, map, omit, pipe, prop } from 'ramda'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAssessmentValues, useSaveAssessmentStep } from '../helpers'

interface Address {
  street?: string
  city?: string
  postalCode?: string
  streetNr?: string
  country?: string
  addressId?: string
}

interface FormValues {
  name?: string
  address?: Address
}

interface CompanyDetails {
  address: Address
}

interface AssessmentValues {
  companyDetails: CompanyDetails
}

const useMessages = () => {
  const { t } = useTranslation()
  return {
    locationName: t('name'),
    locationNameExample: t('locationNameExample'),
    sameAsCompanyName: t('sameAsCompanyName'),
    multipleLocationBannerTitle: t('multipleLocationBannerTitle'),
    multipleLocationBanner: t('multipleLocationBanner'),
    addressLabel: t('addressLabel'),
  }
}

const withoutAddressId = omit(['addressId'])

const makeInitialSameValue = (...p: [FormValues, CompanyDetails]): boolean =>
  // @ts-expect-error remve this after removing ramda
  pipe(map(pipe(prop('address'), withoutAddressId)), spread(equals))(p)

export const LocationDetailsStep = () => {
  const messages = useMessages()
  const { values, setFieldValue } = useFormContext<FormValues>()
  const { save, isLoading } = useSaveAssessmentStep(
    AssessmentStepKeys.locationDetails,
  )
  const {
    values: { companyDetails },
  } = useAssessmentValues() as { values: AssessmentValues }

  const [same, setSame] = useToggable(
    makeInitialSameValue(values as FormValues, companyDetails),
  )

  useOnNextClick(save)

  useNextLoading(() => isLoading, [isLoading])

  useEffect(() => {
    if (!values.address?.street) {
      setFieldValue('address', withoutAddressId(companyDetails.address))
    }
  }, [])

  useEffect(() => {
    if (same && !makeInitialSameValue(values as FormValues, companyDetails))
      setSame(false)

    if (!same && makeInitialSameValue(values as FormValues, companyDetails))
      setSame(true)
  }, [values])

  const handleSameSelect = (e: boolean) => {
    setSame(!e)
    if (e) {
      setFieldValue('address', {
        city: '',
        postalCode: '',
        street: '',
        streetNr: '',
        country: values.address?.country,
      })
    } else {
      setFieldValue('address', withoutAddressId(companyDetails.address))
    }
  }

  return (
    <Column gap="16px">
      <FormInput
        label={messages.locationName}
        name="name"
        defaultValue={values.name}
        noMargin
      />
      <FormLocation name="address" />
      <CheckBox
        checked={same}
        label={messages.sameAsCompanyName}
        onChange={handleSameSelect}
      />
    </Column>
  )
}
